import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { toCompanyAccess } from '@/shared/api/adapt'
import { api } from '@/shared/api/client'
import type { AccessRead } from '@/shared/api/contracts'
import { isApiError } from '@/shared/api/errors'
import { qk } from '@/shared/api/query-keys'
import type { I18n } from '@/shared/i18n'

import type { AccessCreateInput, CompanyAccess, RevealedSecret } from './types'

export function useCompanyAccesses(companyId: string) {
  return useQuery({
    queryKey: qk.companies.accesses(companyId),
    queryFn: async () => {
      const accesses = await api.get<AccessRead[]>(`companies/${companyId}/accesses`)
      return accesses.map(toCompanyAccess)
    },
    enabled: Boolean(companyId),
    // Los accesos no van a disco ni se quedan calientes: es metadata sensible.
    staleTime: 0,
    gcTime: 60_000,
  })
}

/**
 * `POST /accesses/{id}/reveal`.
 *
 * **Es una MUTATION a propósito** (plan 3.5): una query cachearía el secreto en
 * React Query, y de ahí sale en un devtools, en un dump de estado o en la
 * cache que sobrevive al cambio de pantalla. Acá el secreto sale de la red,
 * vive en un `useState` del componente y se limpia a los 30 s y al desmontar.
 *
 * El endpoint está auditado (escribe `access_audit_log` ANTES de responder) y
 * tiene rate-limit de 20/hora: por eso `revealErrorMessage` distingue el 429.
 */
export function useRevealSecret(accessId: string) {
  return useMutation({
    mutationKey: ['accesses', 'reveal', accessId],
    mutationFn: () => api.post<RevealedSecret>(`accesses/${accessId}/reveal`),
    // Ni un retry: cada intento escribe una fila de auditoría y consume cupo
    // del rate-limit.
    retry: false,
    gcTime: 0,
  })
}

/**
 * El 429 del rate-limit no es "un error": es el límite, y se dice como tal.
 *
 * Recibe `t` porque esto no es un componente y no puede usar el hook: el que
 * llama ya tiene el idioma de quien está mirando.
 */
export function revealErrorMessage(error: unknown, t: I18n['t']): string {
  if (isApiError(error) && error.status === 429) {
    return t('accesses.secret.rateLimited')
  }
  if (isApiError(error) && error.isForbidden) {
    return t('accesses.secret.forbidden')
  }
  if (isApiError(error)) return error.message
  return t('accesses.secret.revealError')
}

/**
 * Alta de un acceso.
 *
 * Va a `POST /accesses` con `company_id` en el cuerpo. `POST /companies/{id}/accesses`
 * —lo que el frontend llamaba— no existe; el GET del listado sí cuelga de la
 * empresa, pero el alta no, y esa asimetría era justo la que se había supuesto.
 *
 * `notes` se manda como `notes_internal`, que es como se llama del otro lado.
 */
export function useCreateAccess(companyId: string) {
  const client = useQueryClient()

  return useMutation({
    mutationFn: async (input: AccessCreateInput) => {
      const { notes, ...rest } = input
      return toCompanyAccess(
        await api.post<AccessRead>('accesses', {
          ...rest,
          company_id: companyId,
          notes_internal: notes ?? null,
        }),
      )
    },
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: qk.companies.accesses(companyId) })
    },
  })
}

export function useDeleteAccess(companyId: string) {
  const client = useQueryClient()

  return useMutation({
    mutationFn: (accessId: string) => api.delete<void>(`accesses/${accessId}`),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: qk.companies.accesses(companyId) })
    },
  })
}

/**
 * Deep link al gestor de contraseñas.
 *
 * 1Password publica sus items como `https://start.1password.com/open/i?...`;
 * el mismo path bajo el esquema `onepassword://` abre la app de escritorio en
 * el item exacto en vez de mandar a la web. Si el proveedor es otro, se abre la
 * URL tal cual: mejor un link que funciona que un esquema inventado.
 */
export function vaultDeepLink(access: Pick<CompanyAccess, 'vault_item_url'>): string | null {
  const url = access.vault_item_url
  if (!url) return null
  if (url.startsWith('onepassword://')) return url

  const match = /^https?:\/\/(?:start\.)?1password\.com\/(.*)$/i.exec(url)
  return match ? `onepassword://${match[1]}` : url
}
